import React, { useEffect, useMemo, useReducer, useRef } from 'react';

interface PortraitReelPreviewProps {
  label?: string;
  src: string;
  variant?: 'primary' | 'history';
}

type VideoElement = React.ElementRef<'video'> & {
  webkitEnterFullscreen?: () => void;
};

type PlayerIconName = 'fullscreen' | 'fullscreenExit' | 'mute' | 'pause' | 'play' | 'replay' | 'volume';

interface PlayerState {
  buffered: number;
  controlsVisible: boolean;
  currentTime: number;
  duration: number;
  ended: boolean;
  hasError: boolean;
  isFullscreen: boolean;
  isMuted: boolean;
  isPlaying: boolean;
  isWaiting: boolean;
  volume: number;
}

interface PlayerRefs {
  hideTimerRef: React.MutableRefObject<number | null>;
  playerRef: React.RefObject<HTMLDivElement | null>;
  videoRef: React.RefObject<VideoElement | null>;
}

type PlayerAction =
  | { type: 'reset' }
  | { type: 'update'; value: Partial<PlayerState> };

type PlayerDispatch = React.Dispatch<PlayerAction>;

interface PlayerContext {
  dispatch: PlayerDispatch;
  refs: PlayerRefs;
  state: PlayerState;
}

interface PlayerActions {
  onBlur: React.FocusEventHandler<HTMLDivElement>;
  onFocus: React.FocusEventHandler<HTMLDivElement>;
  onKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
  onPointerLeave: React.PointerEventHandler<HTMLDivElement>;
  onPointerMove: React.PointerEventHandler<HTMLDivElement>;
  seekTo: (nextTime: number) => void;
  setVolume: (nextVolume: number) => void;
  toggleFullscreen: () => void;
  toggleMute: () => void;
  togglePlayback: () => void;
}

interface PlayerController {
  actions: PlayerActions;
  state: PlayerState;
  videoHandlers: React.VideoHTMLAttributes<VideoElement>;
}

interface ReelPlayerHook {
  controller: PlayerController;
  playerRef: React.RefObject<HTMLDivElement | null>;
  videoRef: React.RefObject<VideoElement | null>;
}

const INITIAL_STATE: PlayerState = {
  buffered: 0,
  controlsVisible: true,
  currentTime: 0,
  duration: 0,
  ended: false,
  hasError: false,
  isFullscreen: false,
  isMuted: false,
  isPlaying: false,
  isWaiting: true,
  volume: 1,
};

const PLAYER_ICONS: Record<PlayerIconName, React.ReactNode> = {
  fullscreen: <path d="M7.2 3.5H3.5v3.7M12.8 3.5h3.7v3.7M16.5 12.8v3.7h-3.7M7.2 16.5H3.5v-3.7" />,
  fullscreenExit: <path d="M3.5 7.2h3.7V3.5M16.5 7.2h-3.7V3.5M12.8 16.5v-3.7h3.7M7.2 12.8H3.5v3.7" />,
  mute: (
    <>
      <path d="M3.2 8h3l4-3.2v10.4l-4-3.2h-3V8Z" />
      <path d="m13.4 8 3.4 4M16.8 8l-3.4 4" />
    </>
  ),
  pause: <path d="M6.5 4.5v11M13.5 4.5v11" />,
  play: <path className="reel-player__play-shape" d="m7 4.6 7.8 5.4L7 15.4V4.6Z" />,
  replay: <path d="M5.2 6.1V2.9L2.3 5.8l2.9 2.9V6.1A6.5 6.5 0 1 1 4 11.6" />,
  volume: (
    <>
      <path d="M3.2 8h3l4-3.2v10.4l-4-3.2h-3V8Z" />
      <path d="M13.1 7.2c1.5 1.6 1.5 4 0 5.6M15.3 5.2c2.6 2.7 2.6 6.9 0 9.6" />
    </>
  ),
};

const PlayerIcon: React.FC<{ name: PlayerIconName }> = ({ name }) => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 20 20">
    {PLAYER_ICONS[name]}
  </svg>
);

const finiteMediaTime = (value: number): number => (Number.isFinite(value) && value > 0 ? value : 0);

function formatMediaTime(value: number): string {
  const safeValue = finiteMediaTime(value);
  const minutes = Math.floor(safeValue / 60);
  const seconds = Math.floor(safeValue % 60);

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function playerReducer(state: PlayerState, action: PlayerAction): PlayerState {
  if (action.type === 'reset') return INITIAL_STATE;

  return { ...state, ...action.value };
}

function updatePlayer(context: PlayerContext, value: Partial<PlayerState>): void {
  context.dispatch({ type: 'update', value });
}

function clearHideTimer(context: PlayerContext): void {
  const timer = context.refs.hideTimerRef.current;

  if (timer === null) return;
  window.clearTimeout(timer);
  context.refs.hideTimerRef.current = null;
}

function hideControlsSoon(context: PlayerContext): void {
  clearHideTimer(context);
  context.refs.hideTimerRef.current = window.setTimeout(() => {
    const player = context.refs.playerRef.current;
    const video = context.refs.videoRef.current;

    if (player && video && !video.paused && !player.matches(':focus-within')) {
      updatePlayer(context, { controlsVisible: false });
    }
  }, 2200);
}

function revealControls(context: PlayerContext, autoHide = context.state.isPlaying): void {
  updatePlayer(context, { controlsVisible: true });
  if (autoHide) hideControlsSoon(context);
  else clearHideTimer(context);
}

function isPlaybackAbort(error: unknown): boolean {
  return error instanceof window.DOMException && error.name === 'AbortError';
}

async function startPlayback(context: PlayerContext): Promise<void> {
  const video = context.refs.videoRef.current;

  if (!video) return;
  if (context.state.ended) video.currentTime = 0;
  try {
    await video.play();
  } catch (error) {
    if (!isPlaybackAbort(error)) updatePlayer(context, { hasError: true, isWaiting: false });
  }
}

function togglePlayback(context: PlayerContext): void {
  const video = context.refs.videoRef.current;

  if (!video || context.state.hasError) return;
  revealControls(context, false);
  if (video.paused) void startPlayback(context);
  else video.pause();
}

function restoreAudibleVolume(video: VideoElement): void {
  if (video.volume === 0) video.volume = 1;
  video.muted = false;
}

function toggleMute(context: PlayerContext): void {
  const video = context.refs.videoRef.current;

  if (!video) return;
  if (video.muted || video.volume === 0) restoreAudibleVolume(video);
  else video.muted = true;
  revealControls(context);
}

async function changeFullscreen(player: HTMLDivElement, video: VideoElement): Promise<void> {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    else if (player.requestFullscreen) await player.requestFullscreen();
    else video.webkitEnterFullscreen?.();
  } catch {
    video.webkitEnterFullscreen?.();
  }
}

function toggleFullscreen(context: PlayerContext): void {
  const player = context.refs.playerRef.current;
  const video = context.refs.videoRef.current;

  if (!player || !video) return;
  revealControls(context, false);
  void changeFullscreen(player, video);
}

function seekTo(context: PlayerContext, nextTime: number): void {
  const video = context.refs.videoRef.current;
  const duration = finiteMediaTime(context.state.duration);

  if (!video || duration === 0) return;
  const currentTime = Math.min(Math.max(nextTime, 0), duration);

  video.currentTime = currentTime;
  updatePlayer(context, { currentTime, ended: currentTime >= duration });
  revealControls(context);
}

function setVolume(context: PlayerContext, nextVolume: number): void {
  const video = context.refs.videoRef.current;

  if (!video) return;
  video.volume = nextVolume;
  video.muted = nextVolume === 0;
}

function getTimeShortcut(context: PlayerContext, key: string): (() => void) | undefined {
  if (key === 'arrowleft') return () => seekTo(context, context.state.currentTime - 5);
  if (key === 'arrowright') return () => seekTo(context, context.state.currentTime + 5);
  if (key === 'home') return () => seekTo(context, 0);
  if (key === 'end') return () => seekTo(context, context.state.duration);

  return undefined;
}

function getShortcut(context: PlayerContext, key: string): (() => void) | undefined {
  if (key === ' ' || key === 'k') return () => togglePlayback(context);
  if (key === 'm') return () => toggleMute(context);
  if (key === 'f') return () => toggleFullscreen(context);

  return getTimeShortcut(context, key);
}

function handlePlayerKeyDown(context: PlayerContext, event: React.KeyboardEvent<HTMLDivElement>): void {
  if (event.target !== event.currentTarget) return;
  const shortcut = getShortcut(context, event.key.toLowerCase());

  if (!shortcut) return;
  event.preventDefault();
  shortcut();
}

function handlePlayerBlur(context: PlayerContext, event: React.FocusEvent<HTMLDivElement>): void {
  const nextTarget = event.relatedTarget as Node | null;

  if (!event.currentTarget.contains(nextTarget) && context.state.isPlaying) {
    updatePlayer(context, { controlsVisible: false });
  }
}

function handlePointerLeave(context: PlayerContext): void {
  const player = context.refs.playerRef.current;

  if (context.state.isPlaying && !player?.matches(':focus-within')) {
    updatePlayer(context, { controlsVisible: false });
  }
}

function createPlayerActions(context: PlayerContext): PlayerActions {
  return {
    onBlur: (event) => handlePlayerBlur(context, event),
    onFocus: () => revealControls(context, false),
    onKeyDown: (event) => handlePlayerKeyDown(context, event),
    onPointerLeave: () => handlePointerLeave(context),
    onPointerMove: () => revealControls(context),
    seekTo: (nextTime) => seekTo(context, nextTime),
    setVolume: (nextVolume) => setVolume(context, nextVolume),
    toggleFullscreen: () => toggleFullscreen(context),
    toggleMute: () => toggleMute(context),
    togglePlayback: () => togglePlayback(context),
  };
}

function usePlayerRefs(): PlayerRefs {
  const hideTimerRef = useRef<number | null>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<VideoElement>(null);

  return useMemo(() => ({ hideTimerRef, playerRef, videoRef }), []);
}

function usePlayerEffects(refs: PlayerRefs, dispatch: PlayerDispatch): void {
  useEffect(() => {
    const syncFullscreen = () => dispatch({ type: 'update', value: {
      isFullscreen: document.fullscreenElement === refs.playerRef.current,
    } });

    document.addEventListener('fullscreenchange', syncFullscreen);

    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreen);
      const timer = refs.hideTimerRef.current;

      if (timer !== null) window.clearTimeout(timer);
      refs.videoRef.current?.pause();
    };
  }, [dispatch, refs]);
}

function useReelPlayer(): ReelPlayerHook {
  const refs = usePlayerRefs();
  const [state, dispatch] = useReducer(playerReducer, INITIAL_STATE);
  const context = { dispatch, refs, state };
  const controller = {
    actions: createPlayerActions(context),
    state,
    videoHandlers: createVideoHandlers(context),
  };

  usePlayerEffects(refs, dispatch);

  return {
    controller,
    playerRef: refs.playerRef,
    videoRef: refs.videoRef,
  };
}

function handleLoadedMetadata(context: PlayerContext, video: VideoElement): void {
  updatePlayer(context, {
    duration: finiteMediaTime(video.duration),
    isMuted: video.muted,
    volume: video.volume,
  });
}

function handleProgress(context: PlayerContext, video: VideoElement): void {
  const index = video.buffered.length - 1;
  const buffered = index >= 0 ? video.buffered.end(index) : 0;

  updatePlayer(context, { buffered: finiteMediaTime(buffered) });
}

function handleEnded(context: PlayerContext): void {
  clearHideTimer(context);
  updatePlayer(context, { controlsVisible: true, ended: true, isPlaying: false });
}

function handleError(context: PlayerContext): void {
  clearHideTimer(context);
  updatePlayer(context, {
    controlsVisible: true,
    hasError: true,
    isPlaying: false,
    isWaiting: false,
  });
}

function handlePause(context: PlayerContext): void {
  clearHideTimer(context);
  updatePlayer(context, { controlsVisible: true, isPlaying: false });
}

function handlePlaying(context: PlayerContext): void {
  updatePlayer(context, { controlsVisible: true, isWaiting: false });
  hideControlsSoon(context);
}

function createVideoHandlers(context: PlayerContext): React.VideoHTMLAttributes<VideoElement> {
  return {
    onCanPlay: () => updatePlayer(context, { isWaiting: false }),
    onClick: () => togglePlayback(context),
    onDurationChange: (event) => updatePlayer(context, {
      duration: finiteMediaTime(event.currentTarget.duration),
    }),
    onEnded: () => handleEnded(context),
    onError: () => handleError(context),
    onLoadStart: () => context.dispatch({ type: 'reset' }),
    onLoadedMetadata: (event) => handleLoadedMetadata(context, event.currentTarget),
    onPause: () => handlePause(context),
    onPlay: () => updatePlayer(context, { ended: false, isPlaying: true }),
    onPlaying: () => handlePlaying(context),
    onProgress: (event) => handleProgress(context, event.currentTarget),
    onTimeUpdate: (event) => updatePlayer(context, {
      currentTime: finiteMediaTime(event.currentTarget.currentTime),
    }),
    onVolumeChange: (event) => updatePlayer(context, {
      isMuted: event.currentTarget.muted,
      volume: event.currentTarget.volume,
    }),
    onWaiting: () => updatePlayer(context, { controlsVisible: true, isWaiting: true }),
  };
}

interface ReelVideoProps {
  controller: PlayerController;
  label: string;
  src: string;
  videoRef: React.RefObject<VideoElement | null>;
}

const ReelVideo: React.FC<ReelVideoProps> = ({
  controller,
  label,
  src,
  videoRef,
}) => (
  <video
    {...controller.videoHandlers}
    aria-label={label}
    className="promo-video reel-player__video"
    disablePictureInPicture
    playsInline
    preload="metadata"
    ref={videoRef}
    src={src}
    tabIndex={-1}
  />
);

const PlayerOverlays: React.FC<{ controller: PlayerController }> = ({ controller }) => {
  const { hasError, isPlaying, isWaiting, ended } = controller.state;
  const playLabel = ended ? 'Replay reel' : 'Play reel';

  return (
    <>
      {isWaiting && !hasError && (
        <div aria-live="polite" className="reel-player__loading" role="status">
          <span aria-hidden="true" className="reel-player__spinner" />
          <span className="reel-player__sr-only">Loading reel</span>
        </div>
      )}
      {!isPlaying && !isWaiting && !hasError && (
        <button
          aria-label={playLabel}
          className="reel-player__center-play"
          onClick={controller.actions.togglePlayback}
          title={ended ? 'Replay' : 'Play'}
          type="button"
        >
          <PlayerIcon name={ended ? 'replay' : 'play'} />
        </button>
      )}
      {hasError && <div className="reel-player__error" role="alert">Preview unavailable</div>}
    </>
  );
};

interface SeekBarProps {
  buffered: number;
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
}

const SeekBar: React.FC<SeekBarProps> = ({ buffered, currentTime, duration, onSeek }) => {
  const progressPercent = duration > 0 ? Math.min((currentTime / duration) * 100, 100) : 0;
  const bufferedPercent = duration > 0 ? Math.min((buffered / duration) * 100, 100) : 0;

  return (
    <div className="reel-player__seek">
      <span className="reel-player__seek-buffer" style={{ width: `${bufferedPercent}%` }} />
      <span className="reel-player__seek-progress" style={{ width: `${progressPercent}%` }} />
      <input
        aria-label="Seek reel"
        aria-valuemax={Math.round(duration)}
        aria-valuemin={0}
        aria-valuenow={Math.round(currentTime)}
        aria-valuetext={`${formatMediaTime(currentTime)} of ${formatMediaTime(duration)}`}
        disabled={duration <= 0}
        max={duration || 0}
        min={0}
        onChange={(event) => onSeek(Number(event.target.value))}
        step="0.1"
        type="range"
        value={Math.min(currentTime, duration || 0)}
      />
    </div>
  );
};

const PlaybackButton: React.FC<{ controller: PlayerController }> = ({ controller }) => {
  const { ended, isPlaying } = controller.state;
  const label = isPlaying ? 'Pause reel' : ended ? 'Replay reel' : 'Play reel';

  return (
    <button
      aria-label={label}
      className="reel-player__icon-btn"
      onClick={controller.actions.togglePlayback}
      title={`${label.replace(' reel', '')} (K)`}
      type="button"
    >
      <PlayerIcon name={isPlaying ? 'pause' : ended ? 'replay' : 'play'} />
    </button>
  );
};

const VolumeControl: React.FC<{ controller: PlayerController }> = ({ controller }) => {
  const { isMuted, volume } = controller.state;
  const effectiveVolume = isMuted ? 0 : volume;
  const muted = effectiveVolume === 0;

  return (
    <div className="reel-player__volume">
      <button
        aria-label={muted ? 'Unmute reel' : 'Mute reel'}
        className="reel-player__icon-btn"
        onClick={controller.actions.toggleMute}
        title={muted ? 'Unmute (M)' : 'Mute (M)'}
        type="button"
      >
        <PlayerIcon name={muted ? 'mute' : 'volume'} />
      </button>
      <input
        aria-label="Reel volume"
        aria-valuetext={`${Math.round(effectiveVolume * 100)}%`}
        className="reel-player__volume-range"
        max={1}
        min={0}
        onChange={(event) => controller.actions.setVolume(Number(event.target.value))}
        step="0.05"
        style={{
          background: `linear-gradient(90deg, rgba(244,244,245,.92) ${effectiveVolume * 100}%, rgba(255,255,255,.22) ${effectiveVolume * 100}%)`,
        }}
        type="range"
        value={effectiveVolume}
      />
    </div>
  );
};

const FullscreenButton: React.FC<{ controller: PlayerController }> = ({ controller }) => {
  const { isFullscreen } = controller.state;
  const label = isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen';

  return (
    <button
      aria-label={label}
      className="reel-player__icon-btn"
      onClick={controller.actions.toggleFullscreen}
      title={`${isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} (F)`}
      type="button"
    >
      <PlayerIcon name={isFullscreen ? 'fullscreenExit' : 'fullscreen'} />
    </button>
  );
};

const ControlBar: React.FC<{ controller: PlayerController }> = ({ controller }) => (
  <div className="reel-player__controls">
    <PlaybackButton controller={controller} />
    <span aria-live="off" className="reel-player__time">
      <span>{formatMediaTime(controller.state.currentTime)}</span>
      <span className="reel-player__time-secondary"> / {formatMediaTime(controller.state.duration)}</span>
    </span>
    <span className="reel-player__control-spacer" />
    <VolumeControl controller={controller} />
    <FullscreenButton controller={controller} />
  </div>
);

const PlayerChrome: React.FC<{ controller: PlayerController }> = ({ controller }) => (
  <div className="reel-player__chrome">
    <SeekBar
      buffered={controller.state.buffered}
      currentTime={controller.state.currentTime}
      duration={controller.state.duration}
      onSeek={controller.actions.seekTo}
    />
    <ControlBar controller={controller} />
  </div>
);

function getPlayerClass(controller: PlayerController, variant: 'primary' | 'history'): string {
  const visible = controller.state.controlsVisible || !controller.state.isPlaying || controller.state.isWaiting;

  return [
    'reel-player',
    `reel-player--${variant}`,
    controller.state.isPlaying ? 'reel-player--playing' : '',
    visible ? 'reel-player--controls-visible' : '',
  ].filter(Boolean).join(' ');
}

interface PlayerFrameProps extends PortraitReelPreviewProps {
  controller: PlayerController;
  label: string;
  playerRef: React.RefObject<HTMLDivElement | null>;
  variant: 'primary' | 'history';
  videoRef: React.RefObject<VideoElement | null>;
}

const PlayerFrame: React.FC<PlayerFrameProps> = ({ controller, label, playerRef, src, variant, videoRef }) => (
  <div
    aria-label={`${label} player`}
    className={getPlayerClass(controller, variant)}
    onBlurCapture={controller.actions.onBlur}
    onFocusCapture={controller.actions.onFocus}
    onKeyDown={controller.actions.onKeyDown}
    onPointerLeave={controller.actions.onPointerLeave}
    onPointerMove={controller.actions.onPointerMove}
    ref={playerRef}
    role="group"
    tabIndex={0}
  >
    <ReelVideo controller={controller} label={label} src={src} videoRef={videoRef} />
    <PlayerOverlays controller={controller} />
    <PlayerChrome controller={controller} />
  </div>
);

export const PortraitReelPreview: React.FC<PortraitReelPreviewProps> = ({
  label = 'Generated vertical reel preview',
  src,
  variant = 'primary',
}) => {
  const { controller, playerRef, videoRef } = useReelPlayer();

  return (
    <div className={`promo-preview-media promo-preview-media--${variant}`}>
      <PlayerFrame
        controller={controller}
        label={label}
        playerRef={playerRef}
        src={src}
        variant={variant}
        videoRef={videoRef}
      />
    </div>
  );
};
